// School Management API client
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/school`;

const qs = (params = {}) => {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

const get = (path, params) => axios.get(`${API}${path}${qs(params)}`).then((r) => r.data);
const post = (path, body) => axios.post(`${API}${path}`, body).then((r) => r.data);
const postForm = (path, params, formData) => axios.post(`${API}${path}${qs(params)}`, formData).then((r) => r.data);
const put = (path, body) => axios.put(`${API}${path}`, body).then((r) => r.data);
const del = (path) => axios.delete(`${API}${path}`).then((r) => r.data);

export const schoolApi = {
  // setup
  seedDemo: () => post('/seed-demo'),
  dashboard: (organization_id, academic_year_id) => get('/dashboard', { organization_id, academic_year_id }),
  // Super-admin only: download this org's enrollments as a JSON file (returns the
  // full axios response so the caller can read the blob + Content-Disposition).
  exportEnrollments: () => axios.get(`${API}/admin/export/enrollments`, { responseType: 'blob' }),
  // Super-admin only: dry-run of the 2025-2026 enrollment backfill (no changes made).
  backfillEnrollmentsDryRun: () => post('/admin/backfill/enrollments-2025-2026?dry_run=true'),
  backfillEnrollmentsLive: () => post('/admin/backfill/enrollments-2025-2026?dry_run=false'),
  // Super-admin only: bulk class unenroll (per academic year) + admin audit trail.
  enrollmentRemovalPreview: (organization_id, class_id, academic_year_id) =>
    get(`/classes/${class_id}/enrollment-removal-preview`, { organization_id, academic_year_id }),
  removeClassEnrollments: (class_id, body) => post(`/classes/${class_id}/remove-enrollments`, body),
  listAuditLog: (organization_id, params = {}) => get('/admin/audit-log', { organization_id, ...params }),
  // Super-admin: paired classes (config + bulk sync).
  listClassPairs: (organization_id) => get('/class-pairs', { organization_id }),
  createClassPair: (body) => post('/class-pairs', body),
  updateClassPair: (id, body) => put(`/class-pairs/${id}`, body),
  reverseClassPair: (id) => post(`/class-pairs/${id}/reverse`),
  deleteClassPair: (id) => del(`/class-pairs/${id}`),
  classPairsSyncPreview: (organization_id, academic_year_id) => get('/class-pairs/sync-preview', { organization_id, academic_year_id }),
  classPairsSync: (body) => post('/class-pairs/sync', body),
  // Super-admin: import enrollment from Excel.
  importEnrollmentPreview: (organization_id, academic_year_id, file) => {
    const fd = new FormData();
    if (organization_id) fd.append('organization_id', organization_id);
    if (academic_year_id) fd.append('academic_year_id', academic_year_id);
    fd.append('file', file);
    return axios.post(`${API}/enrollments/import/preview`, fd).then((r) => r.data);
  },
  importEnrollmentApply: (body) => post('/enrollments/import', body),

  // academic years + terms
  listYears: (organization_id) => get('/academic-years', { organization_id }),
  createYear: (body) => post('/academic-years', body),
  updateYear: (id, body) => put(`/academic-years/${id}`, body),
  deleteYear: (id) => del(`/academic-years/${id}`),
  openNewYear: (body) => post('/academic-years/open-new', body),
  backfillYear: (body) => post('/academic-years/backfill-active-year', body),
  promotionsPreview: (body) => post('/promotions/preview', body),
  promotionsApply: (body) => post('/promotions/apply', body),
  clearPromotionProgram: (body) => post('/admin/enrollments/clear-promotion-program', body),
  listTerms: (organization_id, academic_year_id) => get('/terms', { organization_id, academic_year_id }),
  createTerm: (body) => post('/terms', body),
  deleteTerm: (id) => del(`/terms/${id}`),

  // classes + sections
  listClasses: (organization_id, academic_year_id) => get('/classes', { organization_id, academic_year_id }),
  createClass: (body) => post('/classes', body),
  updateClass: (id, body) => put(`/classes/${id}`, body),
  deleteClass: (id) => del(`/classes/${id}`),
  listSections: (organization_id, class_id, academic_year_id) => get('/sections', { organization_id, class_id, academic_year_id }),
  createSection: (body) => post('/sections', body),
  updateSection: (id, body) => put(`/sections/${id}`, body),
  deleteSection: (id) => del(`/sections/${id}`),

  // subjects
  listSubjects: (organization_id, class_id, section_id, academic_year_id) => get('/subjects', { organization_id, class_id, section_id, academic_year_id }),
  createSubject: (body) => post('/subjects', body),
  updateSubject: (id, body) => put(`/subjects/${id}`, body),
  deleteSubject: (id) => del(`/subjects/${id}`),
  copySubjectsToSection: (body) => post('/subjects/copy-to-section', body),

  // subject catalog (master subjects)
  listSubjectCatalog: (organization_id, params = {}) => get('/subjects/catalog', { organization_id, ...params }),
  subjectCatalogNextCode: (organization_id) => get('/subjects/catalog/next-code', { organization_id }),
  createSubjectCatalog: (body) => post('/subjects/catalog', body),
  updateSubjectCatalog: (id, body) => put(`/subjects/catalog/${id}`, body),
  deleteSubjectCatalog: (id) => del(`/subjects/catalog/${id}`),

  // teachers (legacy)
  listTeachers: (organization_id) => get('/teachers', { organization_id }),
  getTeacher: (id) => get(`/teachers/${id}`),
  createTeacher: (body) => post('/teachers', body),
  updateTeacher: (id, body) => put(`/teachers/${id}`, body),
  deleteTeacher: (id) => del(`/teachers/${id}`),

  // teacher/staff (4211xxxx accounts)
  listStaff: (organization_id, params = {}) => get('/staff', { organization_id, ...params }),
  staffCount: (organization_id, params = {}) => get('/staff/count', { organization_id, ...params }),
  staffNextCode: (organization_id) => get('/staff/next-code', { organization_id }),
  getStaff: (id) => get(`/staff/${id}`),
  createStaff: (body) => post('/staff', body),
  updateStaff: (id, body) => put(`/staff/${id}`, body),
  deleteStaff: (id) => del(`/staff/${id}`),
  migrateStaff: (organization_id) => post(`/staff/migrate-from-school-teachers?organization_id=${organization_id}`, {}),
  scanStaffCredentials: (organization_id) => get('/staff/credentials/scan', { organization_id }),
  listStaffWithCredentials: (organization_id) => get('/staff/credentials/list', { organization_id }),
  generateStaffCredentials: (organization_id, staff_ids) => post('/staff/generate-credentials', { organization_id, staff_ids }),
  getStaffCredentials: (id) => get(`/staff/${id}/credentials`),

  // parents (legacy)
  listParents: (organization_id) => get('/parents', { organization_id }),
  createParent: (body) => post('/parents', body),
  updateParent: (id, body) => put(`/parents/${id}`, body),
  deleteParent: (id) => del(`/parents/${id}`),

  // families / guardians (4111xxxx accounts)
  listFamilies: (organization_id, params = {}) => get('/families', { organization_id, ...params }),
  familiesCount: (organization_id, params = {}) => get('/families/count', { organization_id, ...params }),
  familiesNextCode: (organization_id) => get('/families/next-code', { organization_id }),
  getFamily: (id) => get(`/families/${id}`),
  createFamily: (body) => post('/families', body),
  updateFamily: (id, body) => put(`/families/${id}`, body),
  deleteFamily: (id) => del(`/families/${id}`),
  migrateFamilies: (organization_id) => post(`/families/migrate-from-parents?organization_id=${organization_id}`, {}),

  // students
  listStudents: (organization_id, params = {}) => get('/students', { organization_id, ...params }),
  getStudent: (id) => get(`/students/${id}`),
  createStudent: (body) => post('/students', body),
  cleanupStudentAcademicFields: (body) => post('/students/cleanup-academic-fields', body),
  assignDefaultGuardian: (body) => post('/students/assign-default-guardian', body),
  updateStudent: (id, body) => put(`/students/${id}`, body),
  deleteStudent: (id) => del(`/students/${id}`),
  suggestStudentLogin: (organization_id, name) => get('/students/suggest-login', { organization_id, name }),
  getStudentCredentials: (id) => get(`/students/${id}/credentials`),
  scanStudentCredentials: (organization_id) => get('/students/credentials/scan', { organization_id }),
  generateStudentCredentials: (organization_id, student_ids) => post('/students/generate-credentials', { organization_id, student_ids }),
  listStudentsWithCredentials: (organization_id, class_id, section_id, academic_year_id) => get('/students/credentials/list', { organization_id, class_id, section_id, academic_year_id }),
  uploadStudentPhoto: (organization_id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return postForm('/students/photo', { organization_id }, fd);
  },
  enhanceStudentPhoto: (organization_id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return postForm('/students/enhance-photo', { organization_id }, fd);
  },

  // enrollments
  listEnrollments: (organization_id, params = {}) => get('/enrollments', { organization_id, ...params }),
  createEnrollment: (body) => post('/enrollments', body),
  deleteEnrollment: (id) => del(`/enrollments/${id}`),

  // grading scales
  listGradingScales: (organization_id) => get('/grading-scales', { organization_id }),
  createGradingScale: (body) => post('/grading-scales', body),
  updateGradingScale: (id, body) => put(`/grading-scales/${id}`, body),
  deleteGradingScale: (id) => del(`/grading-scales/${id}`),

  // exams + marks
  listExams: (organization_id, params = {}) => get('/exams', { organization_id, ...(typeof params === 'string' ? { academic_year_id: params } : params) }),
  getExam: (id) => get(`/exams/${id}`),
  createExam: (body) => post('/exams', body),
  updateExam: (id, body) => put(`/exams/${id}`, body),
  deleteExam: (id) => del(`/exams/${id}`),
  getMarks: (params) => get('/marks', params),
  marksCompletion: (params) => get('/marks/completion', params),
  saveMarks: (body) => post('/marks', body),
  scaleMarks: (body) => post('/marks/scale', body),
  exportMarksTemplate: (params) => axios.get(`${API}/marks/export-template${qs(params)}`, { responseType: 'blob' }).then((r) => r.data),
  exportMarksWorkbook: (params) => axios.get(`${API}/marks/export-workbook${qs(params)}`, { responseType: 'blob' }).then((r) => r.data),
  importMarksExcel: (params, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return postForm('/marks/import-excel', params, fd);
  },
  importMarksWorkbook: (params, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return postForm('/marks/import-workbook', params, fd);
  },

  // report cards
  getReportCard: (params) => get('/report-cards', params),
  getSectionReport: (organization_id, exam_id, section_id) => get('/report-cards/section', { organization_id, exam_id, section_id }),
  getBatchReport: (params) => get('/report-cards/batch', params),
  getClassAiAnalysis: (params) => get('/report-cards/ai-analysis', params),
  classGradesSummary: (params) => get('/report-cards/class-summary', params),
  classGradesSummaryTermoXlsx: (params) => axios.get(`${API}/report-cards/class-summary/termo-xlsx${qs(params)}`, { responseType: 'blob' }).then((r) => r.data),
  getTermoMapping: (params) => get('/report-cards/termo-mapping', params),
  saveTermoMapping: (body) => post('/report-cards/termo-mapping', body),
  importTermoTemplate: (formData) => axios.post(`${API}/report-cards/termo-template/import`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data),
  generateClassAiAnalysis: (body) => post('/report-cards/ai-analysis', body),
  getReportRemark: (organization_id, exam_id, student_id) => get('/report-remarks', { organization_id, exam_id, student_id }),
  saveReportRemark: (body) => put('/report-remarks', body),

  // attendance
  getAttendance: (organization_id, params = {}) => get('/attendance', { organization_id, ...params }),
  saveAttendance: (body) => post('/attendance', body),
  attendanceSummary: (organization_id, params = {}) => get('/attendance/summary', { organization_id, ...params }),
  attendanceToday: (organization_id, date) => get('/attendance/today', { organization_id, date }),
  myAttendance: (params = {}) => get('/me/attendance', params),

  // portal (me)
  myStudent: (academic_year_id) => get('/me/student', academic_year_id ? { academic_year_id } : {}),
  myParent: () => get('/me/parent'),
  myTeacher: (academic_year_id) => get('/me/teacher', academic_year_id ? { academic_year_id } : {}),
  myTeacherTimetable: () => get('/me/timetable/teacher'),
  myStudentTimetable: () => get('/me/timetable/student'),

  // timetable
  ttGetConfig: (params) => get('/timetable/config', params),
  ttSaveConfig: (body) => post('/timetable/config', body),
  ttResetConfig: (body) => post('/timetable/config/reset', body),
  ttListLessons: (params) => get('/timetable/lessons', params),
  ttSaveLesson: (body) => post('/timetable/lessons', body),
  ttDeleteLesson: (id) => del(`/timetable/lessons/${id}`),
  ttGetAvailability: (params) => get('/timetable/availability', params),
  ttGetAvailabilityAll: (params) => get('/timetable/availability/all', params),
  ttSaveAvailability: (body) => post('/timetable/availability', body),
  ttListEntries: (params) => get('/timetable/entries', params),
  ttUpsertEntry: (body) => post('/timetable/entries', body),
  ttLegalSlots: (body) => post('/timetable/legal-slots', body),
  ttSwapEntries: (body) => post('/timetable/entries/swap', body),
  ttTeacherMove: (body) => post('/timetable/entries/teacher-move', body),
  ttStudentSwap: (body) => post('/timetable/entries/student-swap', body),
  ttLockEntry: (body) => post('/timetable/entries/lock', body),
  ttGetProgramWindows: (params) => get('/timetable/program-windows', params),
  ttSaveProgramWindows: (body) => post('/timetable/program-windows', body),
  ttSubjectNames: (params) => get('/timetable/subject-names', params),
  ttGetMergedSubjects: (params) => get('/timetable/merged-subjects', params),
  ttSaveMergedSubjects: (body) => post('/timetable/merged-subjects', body),
  ttClassSubjectTeachers: (params) => get('/timetable/class-subject-teachers', params),
  ttGetSubjectRules: (params) => get('/timetable/subject-rules', params),
  ttCreateSubjectRule: (body) => post('/timetable/subject-rules', body),
  ttDeleteSubjectRule: (ruleId) => del(`/timetable/subject-rules/${ruleId}`),
  ttClearAll: (body) => post('/timetable/entries/clear-all', body),
  ttResyncUvGroups: (body) => post('/timetable/lessons/resync-uv-groups', body),
  ttRebuildLessons: (body) => post('/timetable/lessons/rebuild-from-assignments', body),
  ttWorkload: (params) => get('/timetable/workload', params),
  ttGetPair: (params) => get('/timetable/pair', params),
  ttSetPair: (body) => post('/timetable/pair', body),
  ttListPairs: (params) => get('/timetable/pairs', params),
  ttGetCycles: (params) => get('/timetable/cycles', params),
  ttSaveCycles: (body) => post('/timetable/cycles', body),
  ttDeleteEntry: (id) => del(`/timetable/entries/${id}`),
  ttClearEntries: (body) => post('/timetable/entries/clear', body),
  ttGenerate: (body) => post('/timetable/generate', body),
  ttGenerateStatus: (jobId) => get(`/timetable/generate/status/${jobId}`),
  ttGenerateCancel: (jobId) => post(`/timetable/generate/cancel/${jobId}`),
  ttAssistant: (body) => post('/timetable/assistant', body),

  // ===== Finance: Fees & Invoicing =====
  listFeeGroups: (organization_id) => get('/fee-groups', { organization_id }),
  createFeeGroup: (body) => post('/fee-groups', body),
  updateFeeGroup: (id, body) => put(`/fee-groups/${id}`, body),
  deleteFeeGroup: (id) => del(`/fee-groups/${id}`),
  listFeeTypes: (organization_id, params = {}) => get('/fee-types', { organization_id, ...params }),
  createFeeType: (body) => post('/fee-types', body),
  updateFeeType: (id, body) => put(`/fee-types/${id}`, body),
  deleteFeeType: (id) => del(`/fee-types/${id}`),
  importFeeTypesPreview: (organization_id, academic_year_id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('organization_id', organization_id);
    if (academic_year_id) fd.append('academic_year_id', academic_year_id);
    return post('/fee-types/import-preview', fd);
  },
  importFeeTypes: (body) => post('/fee-types/import', body),
  listFeeInvoices: (organization_id, params = {}) => get('/fee-invoices', { organization_id, ...params }),
  getFeeInvoice: (id) => get(`/fee-invoices/${id}`),
  createFeeInvoice: (body) => post('/fee-invoices', body),
  bulkInvoicePreview: (body) => post('/fee-invoices/bulk/preview', body),
  bulkCreateInvoices: (body) => post('/fee-invoices/bulk', body),
  classInvoiceRoster: (organization_id, class_id, academic_year_id) => get('/fee-invoices/class-roster', { organization_id, class_id, academic_year_id }),
  updateFeeInvoice: (id, body) => put(`/fee-invoices/${id}`, body),
  deleteFeeInvoice: (id) => del(`/fee-invoices/${id}`),
  postFeeInvoice: (id) => post(`/fee-invoices/${id}/post`, {}),
  unpostFeeInvoice: (id, reason) => post(`/fee-invoices/${id}/unpost`, { reason }),
  // Finance settings, receipts, journal
  getFinanceSettings: (organization_id) => get('/finance-settings', { organization_id }),
  saveFinanceSettings: (body) => put('/finance-settings', body),
  listAccounts: (organization_id, params = {}) => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/accounts${qs({ organization_id, limit: 1000, ...params })}`).then((r) => r.data),
  listReceipts: (organization_id, params = {}) => get('/receipts', { organization_id, ...params }),
  createReceipt: (body) => post('/receipts', body),
  deleteReceipt: (id) => del(`/receipts/${id}`),
  postReceipt: (id) => post(`/receipts/${id}/post`, {}),
  unpostReceipt: (id, reason) => post(`/receipts/${id}/unpost`, { reason }),
  financeJournal: (organization_id, params = {}) => get('/finance-journal', { organization_id, ...params }),
  feesDashboard: (organization_id, params = {}) => get('/fees-dashboard', { organization_id, ...params }),
  guardianStatement: (organization_id, params = {}) => get('/guardian-statement', { organization_id, ...params }),
  overdueReport: (organization_id, params = {}) => get('/overdue', { organization_id, ...params }),
  financeGuardians: (organization_id, params = {}) => get('/guardians', { organization_id, ...params }),
};
